import { Renderable } from "../renderable/Renderable";
import { Vector3, Color } from "flavr-math";
import { RopeFacet } from "flavr-common";
import { InstanceBatchLineMaterial } from "../instancing/lines/lineMaterials";
const SUB_STEP_TIME = 1 / 60;
const SUB_STEP_TIME_SQ = SUB_STEP_TIME * SUB_STEP_TIME;
const STIFFNESS = 1;
const SLEEPING_THRESHOLD = 0.005;
const GRAVITY = new Vector3(0, -9.81, 0);
const TEMP_VECTOR3_1 = new Vector3();
const TEMP_VECTOR3_2 = new Vector3();
const TEMP_VECTOR3_3 = new Vector3();
const TEMP_COLOR = new Color();
export class RopeRenderable extends Renderable {
    constructor(owner) {
        super(owner);
        this.type = RopeRenderable.typestatic;
        this.instances = [];
        this.particles = [];
        this.constraints = [];
        this.pinStartPosition = new Vector3();
        this.pinEndPosition = new Vector3();
        this.isSleeping = false;
        this.timeTaken = 0;
        this.onStartTargetTransformUpdated = (model) => {
            TEMP_VECTOR3_1.setScalar(0);
            if (this.facet !== undefined)
                TEMP_VECTOR3_1.fromArray(this.facet.startOffset).applyQuaternion(model.worldOrientation);
            TEMP_VECTOR3_1.add(model.worldPosition);
            this.pinStartPosition.copy(TEMP_VECTOR3_1);
            this.setSleeping(false);
        };
        this.onEndTargetTransformUpdated = (model) => {
            TEMP_VECTOR3_1.setScalar(0);
            if (this.facet !== undefined)
                TEMP_VECTOR3_1.fromArray(this.facet.endOffset).applyQuaternion(model.worldOrientation);
            TEMP_VECTOR3_1.add(model.worldPosition);
            this.pinEndPosition.copy(TEMP_VECTOR3_1);
            this.setSleeping(false);
        };
        this.simulate = (dt) => {
            var _a;
            if (this.isSleeping)
                return;
            if (this.facet === undefined)
                return;
            if (this.particles.length >= 1) {
                this.particles[0].previous.copy(this.pinStartPosition);
                this.particles[0].position.copy(this.pinStartPosition);
            }
            if (this.particles.length >= 2) {
                const last = this.particles[this.particles.length - 1];
                last.previous.copy(this.pinEndPosition);
                last.position.copy(this.pinEndPosition);
            }
            const force = TEMP_VECTOR3_3.copy(GRAVITY).multiplyScalar(this.facet.gravityScale);
            // force.add(force) // Add wind
            const drag = 1 - this.facet.damping;
            this.timeTaken += dt;
            while (this.timeTaken > SUB_STEP_TIME) {
                this.timeTaken -= SUB_STEP_TIME;
                this.verletIntegrate(SUB_STEP_TIME_SQ, force, drag);
                this.solveConstraints(this.facet.solveIteration);
                if (((_a = this.facet) === null || _a === void 0 ? void 0 : _a.floorHeight) !== undefined) {
                    for (const particle of this.particles) {
                        if (particle.position.y < this.facet.floorHeight) {
                            particle.position.y = this.facet.floorHeight;
                        }
                    }
                }
            }
            this.updateTransform();
            const sleepingThresholdSq = SLEEPING_THRESHOLD * SLEEPING_THRESHOLD;
            let canSleep = true;
            for (const particle of this.particles) {
                if (TEMP_VECTOR3_1.subVectors(particle.position, particle.previous).lengthSq() > sleepingThresholdSq) {
                    canSleep = false;
                    break;
                }
            }
            if (canSleep) {
                this.setSleeping(true);
            }
        };
        this.registerAnimationCallback(this.simulate);
    }
    destroy() {
        this.unregisterAnimationCallback();
        for (const it of this.instances)
            it.destroy();
        this.instances = [];
    }
    setVisible(visible) {
        for (const it of this.instances)
            it.setVisible(visible);
    }
    updateFacet(facet) {
        const numParticles = facet.segments + 1;
        if (this.facet !== undefined) {
            if (!Array.isArray(this.facet.startTarget))
                this.owner.renderer.unregisterTransformUpdate(this.owner.modelUID, RopeFacet, this.facet.startTarget);
            if (!Array.isArray(this.facet.endTarget))
                this.owner.renderer.unregisterTransformUpdate(this.owner.modelUID, RopeFacet, this.facet.endTarget);
        }
        const batch = this.owner.renderer.getLineBatch(this.owner.modelUID, InstanceBatchLineMaterial.FUZZY_LINE);
        this.facet = facet;
        if (!Array.isArray(facet.startTarget)) {
            this.owner.renderer.registerTransformUpdate(this.owner.modelUID, RopeFacet, facet.startTarget, this.onStartTargetTransformUpdated);
        }
        else {
            this.pinStartPosition.fromArray(facet.startTarget);
        }
        if (!Array.isArray(facet.endTarget)) {
            this.owner.renderer.registerTransformUpdate(this.owner.modelUID, RopeFacet, facet.endTarget, this.onEndTargetTransformUpdated);
        }
        else {
            this.pinEndPosition.fromArray(facet.endTarget);
        }
        TEMP_VECTOR3_1.copy(this.pinStartPosition);
        TEMP_VECTOR3_2.copy(this.pinEndPosition);
        const dir = TEMP_VECTOR3_3.subVectors(TEMP_VECTOR3_2, TEMP_VECTOR3_1);
        const segLength = facet.length / facet.segments;
        dir.normalize();
        const numNewParticlesDiff = numParticles - this.particles.length;
        // Create particles that were added
        for (let i = 0; i < numNewParticlesDiff; ++i) {
            this.particles.push({
                position: new Vector3(),
                previous: new Vector3(),
                free: true
            });
        }
        // Remove particles that are no longer needed
        for (let i = numNewParticlesDiff; i < 0; ++i) {
            this.particles.pop();
        }
        // Initialize current and last position
        for (let i = 0, num = this.particles.length - 1; i <= num; ++i) {
            const particle = this.particles[i];
            particle.free = !(i === 0 || i === num);
            particle.position
                .copy(dir)
                .multiplyScalar(segLength * i)
                .add(TEMP_VECTOR3_1);
            particle.previous.copy(particle.position);
        }
        // Create instances line with all particles and add simulation constraints
        for (let i = this.instances.length; i < facet.segments; ++i) {
            const particle = this.particles[i];
            const nextParticle = this.particles[i + 1];
            const it = batch.createInstance();
            it.setStartPoint(particle.position);
            it.setEndPoint(nextParticle.position);
            it.setThickness(facet.thickness);
            it.setColor(TEMP_COLOR.setHex(facet.color));
            this.instances.push(it);
            this.constraints.push({
                p1: particle,
                p2: nextParticle,
                restDistance: segLength
            });
        }
        // Remove unused instances and their simulation constraints
        for (let i = facet.segments, len = this.instances.length; i < len; ++i) {
            const instance = this.instances.pop();
            if (instance !== undefined) {
                instance.destroy();
            }
            this.constraints.pop();
        }
        this.setSleeping(false);
    }
    updateTransform() {
        for (let i = 0, len = this.particles.length - 1; i < len; ++i) {
            const particle = this.particles[i];
            const nextParticle = this.particles[i + 1];
            const it = this.instances[i];
            it.setStartPoint(particle.position);
            it.setEndPoint(nextParticle.position);
        }
        this.setSleeping(false);
    }
    verletIntegrate(deltaTimeSq, force, drag) {
        for (const particle of this.particles) {
            if (!particle.free)
                continue;
            const vel = TEMP_VECTOR3_1.subVectors(particle.position, particle.previous);
            vel.multiplyScalar(drag);
            const newPos = TEMP_VECTOR3_2.copy(force).multiplyScalar(deltaTimeSq);
            newPos.add(particle.position).add(vel);
            particle.previous.copy(particle.position);
            particle.position.copy(newPos);
        }
    }
    solveDistanceConstraint(particleA, particleB, targetDistance) {
        const delta = TEMP_VECTOR3_1.subVectors(particleB.position, particleA.position);
        const currentDist = delta.length();
        const diff = (currentDist - targetDistance) / currentDist;
        const correction = delta.multiplyScalar(diff);
        if (particleA.free && particleB.free) {
            correction.multiplyScalar(0.5);
            particleA.position.add(correction);
            particleB.position.sub(correction);
        }
        else if (particleA.free) {
            particleA.position.add(correction);
        }
        else if (particleB.free) {
            particleB.position.sub(correction);
        }
    }
    solveConstraints(solveIterations) {
        for (let iteration = 0; iteration < solveIterations; ++iteration) {
            for (let i = 0, len = this.constraints.length; i < len; ++i) {
                const constraint = this.constraints[i];
                const particleA = constraint.p1;
                const particleB = constraint.p2;
                this.solveDistanceConstraint(particleA, particleB, constraint.restDistance);
            }
            if (STIFFNESS > 1) {
                const numSegments = this.constraints.length - 1;
                for (let segIdx = 0; segIdx < numSegments; ++segIdx) {
                    const particleA = this.particles[segIdx];
                    let dist = this.constraints[segIdx].restDistance;
                    for (let i = segIdx + 1; i < segIdx + STIFFNESS && i < numSegments; ++i) {
                        const particleB = this.particles[i];
                        dist += this.constraints[i].restDistance;
                        this.solveDistanceConstraint(particleA, particleB, dist);
                    }
                }
            }
        }
    }
    setSleeping(sleeping) {
        if (this.isSleeping === sleeping)
            return;
        this.isSleeping = sleeping;
        if (this.isSleeping) {
            this.unregisterAnimationCallback();
        }
        else {
            this.registerAnimationCallback(this.simulate);
        }
    }
}
RopeRenderable.typestatic = "Rope";
